/**
 * RichTextEditor - WYSIWYG Editor for Edge Labels/Notes
 *
 * A simple WYSIWYG editor using contentEditable that shows
 * formatted text as you type (like Word), not markdown syntax.
 */

import { useRef, useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Link,
  RemoveFormatting,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import DOMPurify from 'dompurify'

interface RichTextEditorProps {
  value: string // HTML content
  onChange: (html: string, plainText: string) => void
  placeholder?: string
  disabled?: boolean
  minHeight?: string
  maxHeight?: string
  className?: string
  /** If true, show only essential buttons for compact mode */
  compact?: boolean
}

interface ToolbarButton {
  icon: React.ElementType
  label: string
  command: string
  value?: string
}

const toolbarButtons: ToolbarButton[] = [
  { icon: Bold, label: 'Negrita (Ctrl+B)', command: 'bold' },
  { icon: Italic, label: 'Cursiva (Ctrl+I)', command: 'italic' },
  { icon: Underline, label: 'Subrayado (Ctrl+U)', command: 'underline' },
  { icon: Strikethrough, label: 'Tachado', command: 'strikeThrough' },
  { icon: Code, label: 'Código', command: 'formatBlock', value: 'pre' },
]

const compactButtons: ToolbarButton[] = [
  { icon: Bold, label: 'Negrita (Ctrl+B)', command: 'bold' },
  { icon: Italic, label: 'Cursiva (Ctrl+I)', command: 'italic' },
  { icon: Code, label: 'Código', command: 'formatBlock', value: 'pre' },
]

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Escribe aquí...',
  disabled = false,
  minHeight = '60px',
  maxHeight = '150px',
  className,
  compact = false,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  // Set initial content (sanitized to prevent XSS)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value)
    }
  }, []) // Only on mount

  // Update content when value changes externally (sanitized to prevent XSS)
  useEffect(() => {
    if (editorRef.current && !isFocused && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value)
    }
  }, [value, isFocused])

  // Execute formatting command
  const execCommand = useCallback((command: string, value?: string) => {
    if (disabled) return
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    handleContentChange()
  }, [disabled])

  // Handle content changes
  const handleContentChange = useCallback(() => {
    if (!editorRef.current) return
    const html = editorRef.current.innerHTML
    const plainText = editorRef.current.textContent || ''
    onChange(html, plainText)
  }, [onChange])

  // Handle paste - strip formatting for plain text paste
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    // Allow rich text paste but sanitize
    // For now, let the browser handle it
  }, [])

  // Clear formatting
  const clearFormatting = useCallback(() => {
    execCommand('removeFormat')
    // Also remove block formatting
    execCommand('formatBlock', 'div')
  }, [execCommand])

  // Insert link
  const insertLink = useCallback(() => {
    const url = prompt('URL del enlace:')
    if (url) {
      execCommand('createLink', url)
    }
  }, [execCommand])

  const buttons = compact ? compactButtons : toolbarButtons
  const showEmpty = !value && !isFocused

  return (
    <div className={cn('border rounded-md', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b px-2 py-1 bg-muted/30">
        <TooltipProvider delayDuration={300}>
          {buttons.map((btn, idx) => {
            const Icon = btn.icon
            return (
              <Tooltip key={idx}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => execCommand(btn.command, btn.value)}
                    disabled={disabled}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {btn.label}
                </TooltipContent>
              </Tooltip>
            )
          })}

          {!compact && (
            <>
              <div className="w-px h-5 bg-border mx-1" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={insertLink}
                    disabled={disabled}
                  >
                    <Link className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Insertar enlace
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={clearFormatting}
                    disabled={disabled}
                  >
                    <RemoveFormatting className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Quitar formato
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </TooltipProvider>
      </div>

      {/* Editor area */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleContentChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onPaste={handlePaste}
          className={cn(
            'px-3 py-2 outline-none overflow-auto',
            'prose prose-sm dark:prose-invert max-w-none',
            'prose-p:my-1 prose-p:leading-relaxed',
            'prose-strong:font-bold prose-em:italic',
            'prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs',
            'prose-pre:bg-muted prose-pre:p-2 prose-pre:rounded prose-pre:text-xs',
            'prose-a:text-primary prose-a:underline',
            '[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-muted-foreground [&:empty]:before:pointer-events-none',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          style={{ minHeight, maxHeight }}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      </div>
    </div>
  )
}

/**
 * Extract plain text from HTML for display on canvas labels
 */
export function htmlToPlainText(html: string): string {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = DOMPurify.sanitize(html)
  return div.textContent || div.innerText || ''
}

/**
 * Extract styled text from HTML for edge labels (preserves some formatting)
 */
export function htmlToStyledText(html: string): string {
  if (!html) return ''
  // Convert <strong> to unicode bold approximation, etc.
  // For simplicity, just return plain text
  return htmlToPlainText(html)
}
